import { WorkflowsCreatePayload } from '@api/types.gen';

import {
  StaggerConfig,
  StaggerOption,
} from '@/react/edge/edge-stacks/components/StaggerFieldset.types';
import { RelativePathModel } from '@/react/portainer/gitops/RelativePathFieldset/types';

import { Artifact, ArtifactFile, FormValues } from '../wizard/types';
import {
  toEnvVarsPayload,
  toFilesPayload,
  toParallelDeploymentConfig,
} from '../wizard/payload';

export function toCreateWorkflowPayload(
  values: FormValues
): WorkflowsCreatePayload {
  const isReconciled = values.engine === 'reconcile';

  return {
    name: values.name,
    engine: values.engine,
    // Only sent for the engine it belongs to, so an Edge stack workflow's
    // payload is unchanged by this field existing.
    reconcile: isReconciled
      ? { interval: values.reconcile.interval }
      : undefined,
    artifacts: values.artifacts.map((artifact) =>
      toArtifactPayload(artifact, isReconciled)
    ),
  };
}

function toArtifactPayload(
  artifact: Artifact,
  isReconciled: boolean
): WorkflowsCreatePayload['artifacts'][number] {
  const valuesFiles =
    isReconciled && artifact.helm.enabled ? artifact.helm.valuesFiles : [];

  return {
    name: artifact.name,
    dependencies: artifact.dependencies,
    deploymentType: artifact.deploymentType,
    files: toFilesPayload(withValuesFiles(artifact.files, valuesFiles)),
    // Only the reconciliation engine accepts environment groups and individual
    // environments, and sending them for an Edge stack has the server reject
    // the artifact.
    targets: isReconciled
      ? {
          edgeGroups: artifact.targets,
          environmentGroups: artifact.environmentGroups,
          environments: artifact.environments,
        }
      : { edgeGroups: artifact.targets },
    config: {
      // Only a isReconciled artifact can be a chart, and only when its single
      // declared path is the chart's directory.
      helm:
        isReconciled && artifact.helm.enabled
          ? {
              chartPath: artifact.files[0]?.path,
              valuesFiles,
              namespace: artifact.helm.namespace || undefined,
            }
          : undefined,
      driftMode: isReconciled ? artifact.config.driftMode : undefined,
      prePullImage: artifact.config.prePullImage,
      retryPeriod: artifact.config.retryDeploy
        ? artifact.config.retryPeriod
        : undefined,
      envVars: toEnvVarsPayload(artifact.config.envVars),
      parallelConfig: toParallelConfig(artifact.config.parallelConfig),
      registries: artifact.config.registryId
        ? [artifact.config.registryId]
        : undefined,
      useManifestNamespaces: artifact.config.useManifestNamespaces,
      kubernetesPrune: artifact.config.kubernetesPrune,
      kubernetesForceApply: artifact.config.kubernetesForceApply,
      ...toRelativePathConfig(artifact.config.relativePath),
    },
  };
}

/**
 * Declares the values files that the artifact's own paths do not already cover.
 *
 * A deployment is materialised from the paths it declares, so a values file
 * kept outside the chart - which is how a repository holding one chart and a
 * directory of per-environment values is laid out - would never reach the
 * environment that has to merge it.
 *
 * One already inside a declared path is left alone: it is in that path's tree
 * already, and declaring it twice is refused as two sources providing the same
 * file.
 */
function withValuesFiles(
  files: Array<ArtifactFile>,
  valuesFiles: Array<string>
): Array<ArtifactFile> {
  function covered(path: string) {
    return files.some(
      (file) => path === file.path || path.startsWith(`${file.path}/`)
    );
  }

  // Nothing to attach the values files to.
  if (files.length === 0) {
    return files;
  }

  const source = files[0];

  return [
    ...files,
    ...valuesFiles
      .filter((path) => !covered(path))
      .map((path) => ({
        sourceId: source.sourceId,
        path,
        ref: source.ref,
      })),
  ];
}

function toRelativePathConfig(relativePath: RelativePathModel) {
  return {
    ...(relativePath.SupportRelativePath
      ? {
          alwaysCloneGitRepoForRelativePath:
            relativePath.AlwaysCloneGitRepoForRelativePath,
          localFilesystemPath: relativePath.FilesystemPath,
        }
      : {}),
    ...(relativePath.SupportPerDeviceConfigs
      ? {
          perDeviceConfigsPath: relativePath.PerDeviceConfigsPath,
          perDeviceConfigsMatchType:
            relativePath.PerDeviceConfigsMatchType || undefined,
          perDeviceConfigsGroupMatchType:
            relativePath.PerDeviceConfigsGroupMatchType || undefined,
        }
      : {}),
  };
}

function toParallelConfig(config: StaggerConfig) {
  if (config.StaggerOption !== StaggerOption.Parallel) {
    return undefined;
  }

  return toParallelDeploymentConfig(config);
}
